import { useHistory } from "react-router-dom";
import { Group, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";

import {
  FindingTriageReason,
  FindingTriageState,
} from "@protos/common/v1/common";
import { IssuesFilter, IssueType } from "@protos/issues/v1/issues";
import {
  updateDeploymentFinding,
  updateDeploymentFindingsBulk,
  updateDeploymentFindingsBulkV2,
  updateSecretsFindings,
} from "@shared/api";
import {
  useCancelFindingsQueries,
  useInvalidateChecklistProgress,
  useInvalidateFindings,
  useOrgAssertDefined,
  useTimeLookbackRange,
} from "@shared/hooks";
import { FindingsFilters, TriageReason, TriageToOptions } from "@shared/types";
import { timeLookbackRangeToSinceDate } from "@shared/utils";

/**
 *
 * @param invalidateQueries optional flag that determines if, after triaging, the relevant findings
 * queries will be invalidated (and thus, refetched). If set to false, instead of invalidatingQueries,
 * `useTriage` will cancel the queries, meaning queries won't be refetched until `invalidateFindings`
 * is explicitly called (or the page refreshes).
 */
export const useTriage = (
  issueType: IssueType,
  invalidateQueries: boolean = true
) => {
  const history = useHistory();
  const org = useOrgAssertDefined();
  const { invalidateFindings, invalidateSecrets } = useInvalidateFindings();
  const { cancelFindingsQueries } = useCancelFindingsQueries();
  const [tlr] = useTimeLookbackRange();
  // used to ensure that checklist updates after first triage
  const { invalidateChecklistProgress } = useInvalidateChecklistProgress();

  const afterTriage = () => {
    invalidateQueries ? invalidateFindings() : cancelFindingsQueries();
    invalidateChecklistProgress();
  };

  const triageFinding = async (
    findingId: number,
    newState: TriageToOptions,
    note: string,
    skipSuccessMessage: boolean = false,
    triageReason?: TriageReason
  ): Promise<boolean> => {
    try {
      await updateDeploymentFinding(org.id, findingId, {
        triageState: newState,
        triageReason,
        note,
      });
      afterTriage();
      if (!skipSuccessMessage) {
        showNotification({
          title: "Success",
          message:
            newState === "reopened"
              ? "Finding successfully reopened"
              : newState === "ignored"
              ? "Finding successfully ignored"
              : newState === "reviewing"
              ? "Finding successfully marked for reviewing"
              : newState === "fixing"
              ? "Finding successfully marked for fixing"
              : "Triage note added",
          color: "green",
        });
      }
      return true;
    } catch (error) {
      // nosemgrep
      console.error(error);
      showNotification({
        title: "Error",
        message:
          newState === "reopened"
            ? "Failed to reopen finding"
            : newState === "ignored"
            ? "Failed to ignore finding"
            : newState === "reviewing"
            ? "Failed to mark finding as reviewing"
            : newState === "fixing"
            ? "Failed to mark finding as fixing"
            : "Something went wrong, please try again",
        color: "red",
      });
      return false;
    }
  };

  type BulkTriageProtoParams = {
    triageState?: FindingTriageState;
    triageReason?: FindingTriageReason;
    note: string;
    ids: number[];
    filterOverrides: FindingsFilters;
  };

  const bulkTriageSecrets = async ({
    triageState,
    triageReason,
    note,
    ids,
    filterOverrides,
  }: BulkTriageProtoParams) => {
    const search = new URLSearchParams(history.location.search);
    Object.entries(filterOverrides).forEach(([key, value]) => {
      if (value) {
        search.set(key, value);
      }
    });

    try {
      search.set("ids", ids.join(","));
      await updateSecretsFindings(
        {
          deploymentId: org.id,
          body: {
            triageState: triageState,
            triageReason: triageReason,
            note,
          },
        },
        search
      );
      invalidateQueries && invalidateSecrets();
    } catch (error) {
      // nosemgrep
      console.error(error);
      showNotification({
        title: "Error",
        message: "Something went wrong, please try again",
        color: "red",
      });
    }
  };

  const bulkTriage = async (
    newState: TriageToOptions,
    note: string,
    triageReason?: TriageReason,
    ids?: number[],
    filterOverrides?: FindingsFilters
  ) => {
    const search = new URLSearchParams(history.location.search);
    if (filterOverrides) {
      Object.entries(filterOverrides).forEach(([key, value]) => {
        if (value) {
          search.set(key, value);
        }
      });
    }

    try {
      // Handle the case where the user selected rule ids and specific findings
      if (filterOverrides?.rule_id) {
        await updateDeploymentFindingsBulk(org.id, search, tlr, issueType, {
          triageState: newState,
          triageReason,
          note,
          ids: [],
        });
        search.delete("rule_id");
      }
      if (filterOverrides?.rule_id === undefined || (ids && ids?.length > 0)) {
        await updateDeploymentFindingsBulk(org.id, search, tlr, issueType, {
          triageState: newState,
          triageReason,
          note,
          ids,
        });
      }
      afterTriage();
      showNotification({
        title: "Success",
        message: (
          <Group>
            <Text>Bulk triage successful.</Text>
          </Group>
        ),
        color: "green",
      });
    } catch (error) {
      // nosemgrep
      console.error(error);
      showNotification({
        title: "Error",
        message: "Something went wrong, please try again",
        color: "red",
      });
    }
  };

  const bulkTriageFindingsV2SuccessMessage = (
    filter: IssuesFilter,
    triageState: FindingTriageState
  ): string => {
    // Single finding has a special message
    if (filter.issueParentIds.length === 1 && filter.ruleIds.length === 0) {
      return triageState === FindingTriageState.REOPENED
        ? "Finding successfully reopened"
        : triageState === FindingTriageState.IGNORED
        ? "Finding successfully ignored"
        : triageState === FindingTriageState.REVIEWING
        ? "Finding successfully marked as reviewing"
        : triageState === FindingTriageState.FIXING
        ? "Finding successfully marked as fixing"
        : "Triage note added";
    }
    // Default
    return "Bulk triage successful.";
  };

  type BulkTriageFindingsProtoParams = {
    filter: IssuesFilter;
    triageState: FindingTriageState;
    triageReason?: FindingTriageReason;
    note?: string;
  };

  const bulkTriageFindingsV2 = async ({
    filter,
    triageState,
    triageReason,
    note,
  }: BulkTriageFindingsProtoParams) => {
    try {
      await updateDeploymentFindingsBulkV2(org.id, {
        deploymentId: org.id,
        issueType: issueType,
        filter: {
          ...filter,
          since: timeLookbackRangeToSinceDate(tlr),
        },
        params: {
          triageState,
          triageReason,
          note,
        },
      });
      afterTriage();
      showNotification({
        title: "Success",
        message: (
          <Group>
            <Text>
              {bulkTriageFindingsV2SuccessMessage(filter, triageState)}
            </Text>
          </Group>
        ),
        color: "green",
      });
    } catch (error) {
      // nosemgrep
      console.error(error);
      showNotification({
        title: "Error",
        message: "Something went wrong, please try again",
        color: "red",
      });
    }
  };

  return {
    bulkTriage,
    bulkTriageFindingsV2,
    bulkTriageSecrets,
    triageFinding,
  };
};
